<script setup lang="ts">
import { onBeforeMount, ref, inject, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { useViewComposable } from '@/views/composable';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';

const dialogRef = inject('dialogRef') as Ref;
const onApply = dialogRef?.value?.options?.emits?.onApply;
const onClose = dialogRef?.value?.options?.emits?.onClose;
const destinations = dialogRef.value.options.data.destinations;
const sheetData = dialogRef.value.options.data.sheetData;
const type = dialogRef.value.options.data.type;

const { t } = useI18n({ useScope: 'global' });
const destinationFilters = ref();
const loading = ref(false);
const sortOrder = ref(0);
const sortField = ref('validateMessage');
const isSort = ref(false);

const sheetDataDestination = ref<any>([]);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const { styleColumnExport, renderNameFileExport } = useViewComposable();
const widthList = [10, 40, 40, 12, 12, 10, 10, 15, 15, 20, 20, 15, 10];
const sheetColumnStyle = styleColumnExport(widthList);

const initDestinationFilters = () => {
  destinationFilters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    nameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    airportName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    airportNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    country: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    countryFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    region: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    regionFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cityName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cityNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    countryName: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    countryNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    code: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    brochure: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    airportCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gatewayType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initDestinationFilters();

onBeforeMount(() => {
  sheetDataDestination.value = [
    {
      name: 'Sheet 1',
      data: sheetData
    }
  ];
});

const handleImportFile = () => {
  onApply();
};

const handleRevert = () => {
  onApply();
};

const closeModal = () => {
  onClose();
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
    default:
      return '';
  }
};

const rowClass = (data: any) => {
  return [{ 'bg-error': data?.validateMessage }];
};

const handleShowMessage = (message: string) => {
  return message ?? t('common.labels.success');
};

const sortMessage = () => {
  isSort.value = !isSort.value;
  sortOrder.value = isSort.value ? -1 : 0;
};
</script>
<template>
  <div class="view-destination-modal">
    <div class="card">
      <DataTable v-model:filters="destinationFilters" :rowClass="rowClass" :value="destinations" paginator showGridlines :loading="loading" :rows="10" :sortField="sortField" :sortOrder="sortOrder" dataKey="code" filterDisplay="menu">
        <template #empty> {{ $t('management.no_data_found') }} </template>
        <template #loading>
          <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
        </template>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportCode" :header="$t('management.package.airport_code')">
          <template #body="{ data }">
            {{ data.airportCode }}
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportName" :header="$t('management.air.airport_name')">
          <template #body="{ data }">
            {{ data.airportName }}
          </template>

          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.airport_name')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="airportNameFr" :header="$t('management.air.airport_name_french')">
          <template #body="{ data }">
            {{ data.airportNameFr }}
          </template>

          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.airport_name_french')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="name" :header="$t('management.package.destination_name')">
          <template #body="{ data }">
            {{ data.name }}
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_destination_name')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="nameFr" :header="$t('management.package.destination_name_french')">
          <template #body="{ data }">
            {{ data.nameFr }}
          </template>

          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_destination_name')" />
          </template>
        </TableColumn>

        <TableColumn :show-filter-operator="false" :show-add-button="false" field="region" :header="$t('management.air.region')">
          <template #body="{ data }">
            {{ data.region }}
          </template>

          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="regionFr" :header="$t('management.air.region_french')">
          <template #body="{ data }">
            {{ data.regionFr }}
          </template>

          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="country" :header="$t('management.air.country_name')">
          <template #body="{ data }">
            {{ data.country }}
          </template>

          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_country')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="countryFr" :header="$t('management.air.country_name_french')">
          <template #body="{ data }">
            {{ data.countryFr }}
          </template>

          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_country')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="status" :header="$t('management.package.dest_status')">
          <template #body="{ data }">
            {{ data.status }}
          </template>
          <template #filter="{ filterModel }">
            <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
              <template #option="slotProps">
                <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
              </template>
            </Select>
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="destType" :header="$t('management.package.dest_type')">
          <template #body="{ data }"> {{ data.destType }} </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_destination_code')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="code" :header="$t('management.package.dest_sv_id')">
          <template #body="{ data }">
            {{ data.code }}
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.air.search_by_destination_code')" />
          </template>
        </TableColumn>
        <TableColumn :show-filter-operator="false" :show-add-button="false" field="brochure" :header="$t('management.package.brochure')">
          <template #body="{ data }">
            {{ data.brochure }}
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('management.package.brochure')" />
          </template>
        </TableColumn>
        <TableColumn class="min-w-200" field="validateMessage" :header="$t('management.hotel.message')" :showAddButton="false" :showFilterOperator="false" sortable>
          <template #sorticon>
            <i class="ml-2 sort_icon" :class="sortOrder === -1 ? 'pi pi-sort-amount-down-alt' : 'pi pi-sort-amount-up-alt'" @click="sortMessage()"></i>
          </template>
          <template #body="{ data, field }">
            <p class="mb-0 break-spaces">{{ handleShowMessage(data[field]) }}</p>
          </template>
        </TableColumn>
      </DataTable>
    </div>
    <div class="flex">
      <div class="w-full text-center" v-if="type === 'IMPORT'">
        <Button id="btn_import_file" icon="pi pi-plus" @click="handleImportFile()" :label="$t('common.buttons.import_file_now')" class="import-button ml-4" />
      </div>
      <div class="w-full text-center" v-if="type === 'REVERT'">
        <Button id="btn_revert_file" icon="pi pi-plus" @click="handleRevert()" :label="$t('common.labels.revert_to_this_version')" class="revert-button ml-4" />
      </div>
      <div class="w-full text-center" v-if="type === 'EXPORT'">
        <ExportExcelFile id="export_btn" class="export ml-4" :sheets="sheetDataDestination" :columnsStyle="sheetColumnStyle" :file-name="renderNameFileExport('Package_Destination_Management')"></ExportExcelFile>
      </div>
      <div>
        <Button id="btn_close" @click="closeModal()" :label="$t('common.buttons.close')" />
      </div>
    </div>
  </div>
</template>
